/**
 * Data hook and URL-state readers for Game Summary.
 *
 * Same split as `use-archive.ts` and `use-tracker.ts`: the page component stays
 * a description of the screen, and everything with a lifecycle — the fetch, the
 * abort, the URL parsing — lives here where it can be reasoned about (and
 * unit-tested) on its own.
 */
import { useCallback, useEffect, useState } from 'react';

import type { ApiClient } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import type { GameSummaryFeed, StatUnit } from '../../lib/api-types';

import { DEFAULT_DRIVE_FILTERS, type DriveFilters, type DriveScopeFilter } from './scoring-summary';

export const SUMMARY_STAT_UNITS: readonly StatUnit[] = ['offense', 'defense', 'special_teams'];

/** Accordion section ids, in phone reading order. */
export const SUMMARY_SECTIONS = ['drives', ...SUMMARY_STAT_UNITS] as const;
export type SummarySectionId = (typeof SUMMARY_SECTIONS)[number];

/** A failure a coach can read — never a stack trace. */
export function readableError(cause: unknown): string {
  if (ApiError.isApiError(cause)) return cause.message;
  return 'The game summary could not be reached. Check your connection and try again.';
}

/** `north-high-eagles` → `North High Eagles`. One tenant portal is one team. */
export function teamNameFromSlug(slug: string | null): string {
  if (slug === null || slug.trim() === '') return 'Our Team';
  return slug
    .split('-')
    .filter((part) => part !== '')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** `?open=` — absent means every section is expanded. */
export function readSections(raw: string | null): Set<SummarySectionId> {
  if (raw === null) return new Set<SummarySectionId>(SUMMARY_SECTIONS);
  const wanted = raw.split(',').map((part) => part.trim());
  return new Set(SUMMARY_SECTIONS.filter((section) => wanted.includes(section)));
}

/** `?poss=` + `?drives=` — the drive table's filters. */
export function readDriveFilters(scope: string | null, mode: string | null): DriveFilters {
  const isScope = scope === 'team' || scope === 'opponent' || scope === 'all';
  return {
    scope: (isScope ? scope : DEFAULT_DRIVE_FILTERS.scope) as DriveScopeFilter,
    isScoringOnly: mode === 'scoring',
  };
}

export interface SummaryFeedState {
  feed: GameSummaryFeed | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * `GET /stats/v1/summary/{event_id}?unit=all`.
 *
 * The previous snapshot stays on screen across a refetch — a live recount must
 * never blank the numbers a coach is mid-read of.
 */
export function useSummaryFeed(apiClient: ApiClient, eventId: string | null): SummaryFeedState {
  const [feed, setFeed] = useState<GameSummaryFeed | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (eventId === null) {
      setFeed(null);
      return undefined;
    }
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .getGameSummary(eventId, { unit: 'all', signal: controller.signal })
      .then((next) => {
        if (controller.signal.aborted) return;
        setError(null);
        setFeed(next);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(readableError(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, eventId, refreshToken]);

  return {
    feed,
    isLoading,
    error,
    reload: useCallback(() => setRefreshToken((token) => token + 1), []),
  };
}
